import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircleIcon, HomeIcon, BuildingIcon, StoreIcon, MapPinIcon, SparklesIcon, ImageIcon, UserIcon, PhoneIcon, MailIcon, EuroIcon, InfoIcon, StarIcon, EyeIcon, ChevronRightIcon, ChevronLeftIcon, Building2Icon, BedDoubleIcon, BathIcon, RulerIcon, CalendarIcon, KeyIcon, DogIcon, CarIcon, TrainIcon, UtensilsIcon, WifiIcon, ZapIcon, DropletIcon, ThermometerIcon, TvIcon, WashingMachineIcon, RefrigeratorIcon, AirVentIcon, SofaIcon, LampIcon, UploadIcon, GripVerticalIcon, AlertCircleIcon, CheckIcon } from 'lucide-react';
interface VisualListingWizardProps {
  onClose: () => void;
  onComplete: (data: any) => void;
}
export function VisualListingWizard({
  onClose,
  onComplete
}: VisualListingWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<Array<{
    id: string;
    url: string;
    file?: File;
    isPrimary: boolean;
  }>>([]);
  const [draggedImageId, setDraggedImageId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    // Step 1: Tip Proprietate
    propertyType: '',
    transactionType: 'inchiriere',
    // Step 2: Locație
    county: 'Bucuresti',
    city: 'Bucuresti',
    zone: '',
    street: '',
    streetNumber: '',
    building: '',
    staircase: '',
    floor: '',
    apartment: '',
    // Step 3: Caracteristici Principale
    rooms: '',
    usefulSurface: '',
    builtSurface: '',
    landSurface: '',
    compartmentalization: 'decomandat',
    floorLevel: '',
    totalFloors: '',
    constructionYear: '',
    // Step 4: Dotări & Facilități
    heating: 'centrala_termica',
    ac: false,
    furnished: 'nemobilat',
    kitchen: 'separata',
    bathrooms: '1',
    balcony: false,
    balconyCount: '',
    terrace: false,
    terraceSize: '',
    parking: 'nu',
    parkingType: '',
    parkingSpaces: '',
    storage: false,
    elevator: false,
    accessibility: false,
    // Utilities
    water: true,
    sewerage: true,
    electricity: true,
    gas: true,
    internet: false,
    cableTv: false,
    // Appliances & Furniture
    fridge: false,
    stove: false,
    oven: false,
    washingMachine: false,
    dishwasher: false,
    microwave: false,
    tv: false,
    bed: false,
    wardrobe: false,
    sofa: false,
    diningTable: false,
    // Reguli & Restricții
    petFriendly: 'nu',
    smokingAllowed: false,
    // Step 5: Preț & Condiții
    price: '',
    currency: 'EUR',
    negotiable: false,
    utilitiesIncluded: false,
    utilitiesCost: '',
    deposit: '1',
    advancePayment: '1',
    minimumRentalPeriod: '12',
    // Comisioane
    ownerCommission: '50',
    tenantCommission: '50',
    // Step 6: Contact & Disponibilitate
    ownerType: 'persoana_fizica',
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    availability: 'imediat',
    availabilityDate: '',
    viewingSchedule: 'program',
    // Step 7: Descriere
    title: '',
    description: '',
    // Step 8: Imagini
    images: [] as string[],
    // Step 9: Promovare
    publishStoria: true,
    publishOLX: true,
    publishImobiliare: true,
    publishOwnSite: true,
    featuredListing: false,
    autoRenew: true
  });
  const totalSteps = 9;
  const progress = currentStep / totalSteps * 100;
  // Auto-calculate commissions
  useEffect(() => {
    if (formData.price) {
      const price = parseFloat(formData.price);
      if (!isNaN(price)) {
        const ownerComm = (price * parseFloat(formData.ownerCommission) / 100 * 1.19).toFixed(0);
        const tenantComm = (price * parseFloat(formData.tenantCommission) / 100 * 1.19).toFixed(0);
        // Store in a separate state if needed, or just display calculated values
      }
    }
  }, [formData.price, formData.ownerCommission, formData.tenantCommission]);
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleComplete = () => {
    const completeData = {
      ...formData,
      images: uploadedImages.map(img => img.url)
    };
    onComplete(completeData);
  };
  const handleGenerateDescription = () => {
    setIsGenerating(true);
    setTimeout(() => {
      const propertyName = formData.propertyType === 'apartament' ? 'Apartament' : formData.propertyType === 'casa' ? 'Casă' : formData.propertyType === 'vila' ? 'Vilă' : 'Spațiu';
      const title = `${propertyName} ${formData.rooms} ${formData.rooms === '1' ? 'cameră' : 'camere'}, ${formData.usefulSurface}mp, ${formData.zone}`;
      const desc = `${propertyName} modern cu ${formData.rooms} ${formData.rooms === '1' ? 'cameră' : 'camere'} de închiriat în ${formData.zone}, ${formData.city}.

📐 Suprafață: ${formData.usefulSurface}mp utili${formData.builtSurface ? `, ${formData.builtSurface}mp construiți` : ''}
🏢 Etaj: ${formData.floorLevel}${formData.totalFloors ? ` din ${formData.totalFloors}` : ''}
📅 An construcție: ${formData.constructionYear || 'N/A'}
🔑 Compartimentare: ${formData.compartmentalization === 'decomandat' ? 'Decomandat' : 'Semidecomandat'}

${formData.bathrooms !== '1' ? `🚿 ${formData.bathrooms} băi\n` : ''}${formData.balcony ? `🌿 Balcon${formData.balconyCount ? ` (${formData.balconyCount})` : ''}\n` : ''}${formData.terrace ? `☀️ Terasă ${formData.terraceSize}mp\n` : ''}${formData.parking !== 'nu' ? `🚗 Loc parcare ${formData.parkingType}\n` : ''}
🔥 Încălzire: ${formData.heating === 'centrala_termica' ? 'Centrală termică' : formData.heating === 'centrala_proprie' ? 'Centrală proprie' : 'Centrală de bloc'}
${formData.ac ? '❄️ Aer condiționat\n' : ''}${formData.elevator ? '🛗 Lift\n' : ''}
📦 Mobilat: ${formData.furnished === 'mobilat' ? 'Complet mobilat' : formData.furnished === 'semimobilat' ? 'Semimobilat' : 'Nemobilat'}

${formData.petFriendly === 'da' ? '🐕 Se acceptă animale de companie\n' : ''}
💰 Preț: ${formData.price} ${formData.currency}/lună${formData.utilitiesIncluded ? ' (utilități incluse)' : ' + utilități'}
💵 Garanție: ${formData.deposit} ${formData.deposit === '1' ? 'lună' : 'luni'} chirie

Disponibil ${formData.availability === 'imediat' ? 'imediat' : `din ${formData.availabilityDate}`}.

Pentru programare vizionare, contactați: ${formData.ownerPhone}`;
      setFormData(prev => ({
        ...prev,
        title,
        description: desc
      }));
      setIsGenerating(false);
    }, 2000);
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newImages = Array.from(files).map((file, i) => ({
      id: `img-${Date.now()}-${i}`,
      url: URL.createObjectURL(file),
      file: file,
      isPrimary: uploadedImages.length === 0 && i === 0
    }));
    setUploadedImages([...uploadedImages, ...newImages]);
  };
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  const handleImageDragStart = (imageId: string) => {
    setDraggedImageId(imageId);
  };
  const handleImageDragOver = (e: React.DragEvent, targetImageId: string) => {
    e.preventDefault();
    if (!draggedImageId || draggedImageId === targetImageId) return;
    const draggedIndex = uploadedImages.findIndex(img => img.id === draggedImageId);
    const targetIndex = uploadedImages.findIndex(img => img.id === targetImageId);
    const newImages = [...uploadedImages];
    const [draggedImage] = newImages.splice(draggedIndex, 1);
    newImages.splice(targetIndex, 0, draggedImage);
    setUploadedImages(newImages);
  };
  const handleImageDragEnd = () => {
    setDraggedImageId(null);
  };
  const setPrimaryImage = (imageId: string) => {
    setUploadedImages(uploadedImages.map(img => ({
      ...img,
      isPrimary: img.id === imageId
    })));
  };
  const removeImage = (imageId: string) => {
    const imageToRemove = uploadedImages.find(img => img.id === imageId);
    if (imageToRemove?.url.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.url);
    }
    setUploadedImages(uploadedImages.filter(img => img.id !== imageId));
  };
  const propertyTypes = [{
    id: 'apartament',
    label: 'Apartament',
    icon: BuildingIcon,
    color: 'from-blue-500 to-blue-600'
  }, {
    id: 'casa',
    label: 'Casă',
    icon: HomeIcon,
    color: 'from-green-500 to-green-600'
  }, {
    id: 'vila',
    label: 'Vilă',
    icon: Building2Icon,
    color: 'from-purple-500 to-purple-600'
  }, {
    id: 'spatiu-comercial',
    label: 'Spațiu Comercial',
    icon: StoreIcon,
    color: 'from-orange-500 to-orange-600'
  }];
  const counties = ['Bucuresti', 'Ilfov', 'Cluj', 'Timis', 'Brasov', 'Constanta', 'Iasi', 'Prahova'];
  const cities: Record<string, string[]> = {
    Bucuresti: ['Bucuresti'],
    Ilfov: ['Voluntari', 'Popesti-Leordeni', 'Bragadiru', 'Chitila', 'Otopeni'],
    Cluj: ['Cluj-Napoca', 'Floresti', 'Baciu'],
    Timis: ['Timisoara', 'Dumbravita', 'Ghiroda'],
    Brasov: ['Brasov', 'Sacele', 'Codlea'],
    Constanta: ['Constanta', 'Mamaia', 'Eforie Nord'],
    Iasi: ['Iasi'],
    Prahova: ['Ploiesti', 'Sinaia', 'Busteni']
  };
  const popularZones: Record<string, string[]> = {
    Bucuresti: ['Centru Vechi', 'Unirii', 'Romana', 'Victoriei', 'Universitate', 'Piata Muncii', 'Titan', 'Drumul Taberei', 'Militari', 'Berceni', 'Pantelimon', 'Vitan', 'Dristor', 'Obor', 'Colentina'],
    'Cluj-Napoca': ['Centru', 'Marasti', 'Manastur', 'Gheorgheni', 'Zorilor', 'Grigorescu'],
    Timisoara: ['Centru', 'Fabric', 'Elisabetin', 'Ciresica', 'Dambovita']
  };
  const stepTitles = ['Tip Proprietate', 'Locație', 'Caracteristici', 'Dotări & Facilități', 'Preț & Condiții', 'Contact', 'Descriere', 'Imagini', 'Promovare'];
  return <div className="fixed inset-0 z-50 bg-white" data-id="element-6042">
      {/* Header with Progress */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm" data-id="element-6043">
        <div className="max-w-5xl mx-auto px-6 py-4" data-id="element-6044">
          <div className="flex items-center justify-between mb-3" data-id="element-6045">
            <div className="flex items-center space-x-4" data-id="element-6046">
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6047">
                <XIcon className="w-5 h-5 text-gray-600" data-id="element-6048" />
              </button>
              <div data-id="element-6049">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-6050">
                  Creare Anunț Închiriere
                </h2>
                <p className="text-sm text-gray-500" data-id="element-6051">
                  Pasul {currentStep} din {totalSteps}:{' '}
                  {stepTitles[currentStep - 1]}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-6052">
              {currentStep > 1 && <button onClick={prevStep} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1" data-id="element-6053">
                  <ChevronLeftIcon className="w-4 h-4" data-id="element-6054" />
                  <span data-id="element-6055">Înapoi</span>
                </button>}
              {currentStep < totalSteps ? <button onClick={nextStep} className="px-6 py-2 bg-[#357BEB] text-white rounded-lg font-semibold hover:bg-[#2868d8] transition-colors flex items-center space-x-1" data-id="element-6056">
                  <span data-id="element-6057">Continuă</span>
                  <ChevronRightIcon className="w-4 h-4" data-id="element-6058" />
                </button> : <button onClick={handleComplete} className="px-6 py-2 bg-[#21A366] text-white rounded-lg font-semibold hover:bg-[#1a8555] transition-colors flex items-center space-x-2" data-id="element-6059">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-6060" />
                  <span data-id="element-6061">Publică Anunțul</span>
                </button>}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-6062">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-full" data-id="element-6063" />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between mt-3" data-id="element-6064">
            {stepTitles.map((title, index) => <div key={index} className={`flex flex-col items-center ${index + 1 === currentStep ? 'opacity-100' : 'opacity-40'}`} data-id="element-6065">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${index + 1 < currentStep ? 'bg-[#21A366] text-white' : index + 1 === currentStep ? 'bg-[#357BEB] text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-6066">
                  {index + 1 < currentStep ? <CheckIcon className="w-4 h-4" data-id="element-6067" /> : index + 1}
                </div>
                <span className="text-xs mt-1 hidden md:block" data-id="element-6068">{title}</span>
              </div>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="h-[calc(100vh-180px)] overflow-y-auto" data-id="element-6069">
        <div className="max-w-4xl mx-auto px-6 py-8" data-id="element-6070">
          <AnimatePresence mode="wait" data-id="element-6071">
            {/* Step 1: Tip Proprietate */}
            {currentStep === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6072">
                <div className="text-center mb-8" data-id="element-6073">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6074">
                    Ce tip de proprietate închiriezi?
                  </h1>
                  <p className="text-gray-600" data-id="element-6075">
                    Selectează categoria potrivită pentru anunțul tău
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-6076">
                  {propertyTypes.map(type => {
                const Icon = type.icon;
                const isSelected = formData.propertyType === type.id;
                return <button key={type.id} onClick={() => setFormData({
                  ...formData,
                  propertyType: type.id
                })} className={`relative p-8 rounded-2xl border-2 transition-all ${isSelected ? 'border-[#357BEB] bg-blue-50 shadow-lg scale-105' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}`} data-id="element-6077">
                        <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center`} data-id="element-6078">
                          <Icon className="w-8 h-8 text-white" data-id="element-6079" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 text-center" data-id="element-6080">
                          {type.label}
                        </h3>
                        {isSelected && <div className="absolute top-3 right-3" data-id="element-6081">
                            <CheckCircleIcon className="w-6 h-6 text-[#21A366]" data-id="element-6082" />
                          </div>}
                      </button>;
              })}
                </div>
              </motion.div>}

            {/* Step 2: Locație */}
            {currentStep === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6083">
                <div className="text-center mb-8" data-id="element-6084">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6085">
                    Unde este localizată proprietatea?
                  </h1>
                  <p className="text-gray-600" data-id="element-6086">Completează adresa exactă</p>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-6" data-id="element-6087">
                  <div className="grid grid-cols-2 gap-4" data-id="element-6088">
                    <div data-id="element-6089">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6090">
                        Județ *
                      </label>
                      <select value={formData.county} onChange={e => setFormData({
                    ...formData,
                    county: e.target.value,
                    city: cities[e.target.value]?.[0] || '',
                    zone: ''
                  })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6091">
                        {counties.map(county => <option key={county} value={county} data-id="element-6092">
                            {county}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-6093">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6094">
                        Oraș *
                      </label>
                      <select value={formData.city} onChange={e => setFormData({
                    ...formData,
                    city: e.target.value,
                    zone: ''
                  })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6095">
                        {(cities[formData.county] || []).map(city => <option key={city} value={city} data-id="element-6096">
                            {city}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-6097">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6098">
                      Zonă / Cartier *
                    </label>
                    <input type="text" value={formData.zone} onChange={e => setFormData({
                  ...formData,
                  zone: e.target.value
                })} placeholder="Ex: Centru, Titan, Militari..." className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6099" />
                    {popularZones[formData.city] && <div className="mt-3 flex flex-wrap gap-2" data-id="element-6100">
                        <span className="text-xs text-gray-500" data-id="element-6101">
                          Zone populare:
                        </span>
                        {popularZones[formData.city].slice(0, 8).map(zone => <button key={zone} onClick={() => setFormData({
                    ...formData,
                    zone
                  })} className="px-3 py-1 text-xs bg-gray-100 hover:bg-[#357BEB] hover:text-white rounded-full transition-colors" data-id="element-6102">
                            {zone}
                          </button>)}
                      </div>}
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-6103">
                    <div data-id="element-6104">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6105">
                        Stradă *
                      </label>
                      <input type="text" value={formData.street} onChange={e => setFormData({
                    ...formData,
                    street: e.target.value
                  })} placeholder="Ex: Bulevardul Unirii" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6106" />
                    </div>
                    <div data-id="element-6107">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6108">
                        Număr *
                      </label>
                      <input type="text" value={formData.streetNumber} onChange={e => setFormData({
                    ...formData,
                    streetNumber: e.target.value
                  })} placeholder="Ex: 45" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6109" />
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4" data-id="element-6110">
                    <div data-id="element-6111">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6112">
                        Bloc
                      </label>
                      <input type="text" value={formData.building} onChange={e => setFormData({
                    ...formData,
                    building: e.target.value
                  })} placeholder="A1" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6113" />
                    </div>
                    <div data-id="element-6114">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6115">
                        Scară
                      </label>
                      <input type="text" value={formData.staircase} onChange={e => setFormData({
                    ...formData,
                    staircase: e.target.value
                  })} placeholder="2" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6116" />
                    </div>
                    <div data-id="element-6117">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6118">
                        Etaj
                      </label>
                      <input type="text" value={formData.floor} onChange={e => setFormData({
                    ...formData,
                    floor: e.target.value
                  })} placeholder="3" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6119" />
                    </div>
                    <div data-id="element-6120">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6121">
                        Ap.
                      </label>
                      <input type="text" value={formData.apartment} onChange={e => setFormData({
                    ...formData,
                    apartment: e.target.value
                  })} placeholder="15" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6122" />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3" data-id="element-6123">
                    <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-6124" />
                    <div className="text-sm text-gray-700" data-id="element-6125">
                      <p className="font-semibold mb-1" data-id="element-6126">Confidențialitate</p>
                      <p data-id="element-6127">
                        Adresa exactă nu va fi afișată public. Doar zona și
                        cartierul vor fi vizibile în anunț.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>}

            {/* Step 3: Caracteristici Principale */}
            {currentStep === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6128">
                <div className="text-center mb-8" data-id="element-6129">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6130">
                    Caracteristici Principale
                  </h1>
                  <p className="text-gray-600" data-id="element-6131">
                    Detalii esențiale despre proprietate
                  </p>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-6" data-id="element-6132">
                  <div data-id="element-6133">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-6134">
                      Număr Camere *
                    </label>
                    <div className="grid grid-cols-6 gap-3" data-id="element-6135">
                      {['1', '2', '3', '4', '5', '6+'].map(num => <button key={num} onClick={() => setFormData({
                    ...formData,
                    rooms: num
                  })} className={`py-4 rounded-xl font-bold text-lg transition-all ${formData.rooms === num ? 'bg-[#357BEB] text-white shadow-lg scale-110' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6136">
                          {num}
                        </button>)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-6137">
                    <div data-id="element-6138">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6139">
                        <RulerIcon className="w-4 h-4 inline mr-1" data-id="element-6140" />
                        Suprafață Utilă (mp) *
                      </label>
                      <input type="number" value={formData.usefulSurface} onChange={e => setFormData({
                    ...formData,
                    usefulSurface: e.target.value
                  })} placeholder="52" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6141" />
                    </div>
                    <div data-id="element-6142">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6143">
                        <RulerIcon className="w-4 h-4 inline mr-1" data-id="element-6144" />
                        Suprafață Construită (mp)
                      </label>
                      <input type="number" value={formData.builtSurface} onChange={e => setFormData({
                    ...formData,
                    builtSurface: e.target.value
                  })} placeholder="60" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6145" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-6146">
                    <div data-id="element-6147">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6148">
                        Compartimentare
                      </label>
                      <select value={formData.compartmentalization} onChange={e => setFormData({
                    ...formData,
                    compartmentalization: e.target.value
                  })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6149">
                        <option value="decomandat" data-id="element-6150">Decomandat</option>
                        <option value="semidecomandat" data-id="element-6151">Semidecomandat</option>
                        <option value="circular" data-id="element-6152">Circular</option>
                        <option value="vagon" data-id="element-6153">Vagon</option>
                      </select>
                    </div>
                    <div data-id="element-6154">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6155">
                        <CalendarIcon className="w-4 h-4 inline mr-1" data-id="element-6156" />
                        An Construcție
                      </label>
                      <input type="number" value={formData.constructionYear} onChange={e => setFormData({
                    ...formData,
                    constructionYear: e.target.value
                  })} placeholder="2010" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6157" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-6158">
                    <div data-id="element-6159">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6160">
                        Etaj
                      </label>
                      <select value={formData.floorLevel} onChange={e => setFormData({
                    ...formData,
                    floorLevel: e.target.value
                  })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6161">
                        <option value="" data-id="element-6162">Selectează</option>
                        <option value="parter" data-id="element-6163">Parter</option>
                        <option value="1" data-id="element-6164">Etaj 1</option>
                        <option value="2" data-id="element-6165">Etaj 2</option>
                        <option value="3" data-id="element-6166">Etaj 3</option>
                        <option value="4" data-id="element-6167">Etaj 4</option>
                        <option value="5" data-id="element-6168">Etaj 5</option>
                        <option value="6+" data-id="element-6169">Etaj 6+</option>
                        <option value="mansarda" data-id="element-6170">Mansardă</option>
                      </select>
                    </div>
                    <div data-id="element-6171">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6172">
                        Total Etaje Clădire
                      </label>
                      <input type="text" value={formData.totalFloors} onChange={e => setFormData({
                    ...formData,
                    totalFloors: e.target.value
                  })} placeholder="Ex: 4, P+4" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] transition-colors" data-id="element-6173" />
                    </div>
                  </div>
                </div>
              </motion.div>}

            {/* Step 4: Dotări & Facilități */}
            {currentStep === 4 && <motion.div key="step4" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6174">
                <div className="text-center mb-8" data-id="element-6175">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6176">
                    Dotări & Facilități
                  </h1>
                  <p className="text-gray-600" data-id="element-6177">Ce oferă proprietatea?</p>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-8" data-id="element-6178">
                  {/* Încălzire & Climatizare */}
                  <div data-id="element-6179">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-6180">
                      <ThermometerIcon className="w-5 h-5 mr-2 text-[#357BEB]" data-id="element-6181" />
                      Încălzire & Climatizare
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-6182">
                      <div data-id="element-6183">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6184">
                          Sistem Încălzire
                        </label>
                        <select value={formData.heating} onChange={e => setFormData({
                      ...formData,
                      heating: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6185">
                          <option value="centrala_termica" data-id="element-6186">
                            Centrală Termică (RADET)
                          </option>
                          <option value="centrala_proprie" data-id="element-6187">
                            Centrală Proprie
                          </option>
                          <option value="aer_conditionat" data-id="element-6188">
                            Aer Condiționat
                          </option>
                          <option value="sobe" data-id="element-6189">Sobe</option>
                          <option value="fara" data-id="element-6190">Fără</option>
                        </select>
                      </div>
                      <div className="flex items-end" data-id="element-6191">
                        <label className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 w-full" data-id="element-6192">
                          <input type="checkbox" checked={formData.ac} onChange={e => setFormData({
                        ...formData,
                        ac: e.target.checked
                      })} className="w-5 h-5 text-[#357BEB] rounded" data-id="element-6193" />
                          <span className="font-semibold text-gray-900" data-id="element-6194">
                            Aer Condiționat
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Mobilare */}
                  <div data-id="element-6195">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-6196">
                      <SofaIcon className="w-5 h-5 mr-2 text-[#357BEB]" data-id="element-6197" />
                      Mobilare
                    </h3>
                    <div className="grid grid-cols-3 gap-3" data-id="element-6198">
                      {[{
                    value: 'nemobilat',
                    label: 'Nemobilat'
                  }, {
                    value: 'semimobilat',
                    label: 'Semimobilat'
                  }, {
                    value: 'mobilat',
                    label: 'Mobilat Complet'
                  }].map(option => <button key={option.value} onClick={() => setFormData({
                    ...formData,
                    furnished: option.value
                  })} className={`py-3 px-4 rounded-xl font-semibold transition-all ${formData.furnished === option.value ? 'bg-[#357BEB] text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6199">
                          {option.label}
                        </button>)}
                    </div>
                  </div>

                  {/* Băi & Bucătărie */}
                  <div data-id="element-6200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-6201">
                      <BathIcon className="w-5 h-5 mr-2 text-[#357BEB]" data-id="element-6202" />
                      Băi & Bucătărie
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-6203">
                      <div data-id="element-6204">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6205">
                          Număr Băi
                        </label>
                        <select value={formData.bathrooms} onChange={e => setFormData({
                      ...formData,
                      bathrooms: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6206">
                          <option value="1" data-id="element-6207">1 baie</option>
                          <option value="2" data-id="element-6208">2 băi</option>
                          <option value="3" data-id="element-6209">3 băi</option>
                          <option value="4+" data-id="element-6210">4+ băi</option>
                        </select>
                      </div>
                      <div data-id="element-6211">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6212">
                          Tip Bucătărie
                        </label>
                        <select value={formData.kitchen} onChange={e => setFormData({
                      ...formData,
                      kitchen: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6213">
                          <option value="separata" data-id="element-6214">Separată</option>
                          <option value="deschisa" data-id="element-6215">
                            Deschisă (Open Space)
                          </option>
                          <option value="semideschisa" data-id="element-6216">Semideschisă</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Parcare */}
                  <div data-id="element-6217">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-6218">
                      <CarIcon className="w-5 h-5 mr-2 text-[#357BEB]" data-id="element-6219" />
                      Parcare
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-6220">
                      <div data-id="element-6221">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6222">
                          Disponibilitate Parcare
                        </label>
                        <select value={formData.parking} onChange={e => setFormData({
                      ...formData,
                      parking: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6223">
                          <option value="nu" data-id="element-6224">Fără parcare</option>
                          <option value="da" data-id="element-6225">Cu parcare</option>
                        </select>
                      </div>
                      {formData.parking === 'da' && <div data-id="element-6226">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6227">
                            Tip Parcare
                          </label>
                          <select value={formData.parkingType} onChange={e => setFormData({
                      ...formData,
                      parkingType: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6228">
                            <option value="" data-id="element-6229">Selectează</option>
                            <option value="subteran" data-id="element-6230">Subterană</option>
                            <option value="suprafata" data-id="element-6231">La suprafață</option>
                            <option value="curte" data-id="element-6232">În curte</option>
                            <option value="strada" data-id="element-6233">Pe stradă</option>
                          </select>
                        </div>}
                    </div>
                  </div>

                  {/* Facilități Suplimentare */}
                  <div data-id="element-6234">
                    <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-6235">
                      Facilități Suplimentare
                    </h3>
                    <div className="grid grid-cols-2 gap-3" data-id="element-6236">
                      {[{
                    key: 'balcony',
                    label: 'Balcon',
                    icon: '🌿'
                  }, {
                    key: 'terrace',
                    label: 'Terasă',
                    icon: '☀️'
                  }, {
                    key: 'storage',
                    label: 'Boxă/Depozitare',
                    icon: '📦'
                  }, {
                    key: 'elevator',
                    label: 'Lift',
                    icon: '🛗'
                  }, {
                    key: 'accessibility',
                    label: 'Acces Persoane Dizabilități',
                    icon: '♿'
                  }, {
                    key: 'internet',
                    label: 'Internet',
                    icon: '📡'
                  }].map(item => <label key={item.key} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors" data-id="element-6237">
                          <input type="checkbox" checked={formData[item.key as keyof typeof formData] as boolean} onChange={e => setFormData({
                      ...formData,
                      [item.key]: e.target.checked
                    })} className="w-5 h-5 text-[#357BEB] rounded" data-id="element-6238" />
                          <span className="text-xl" data-id="element-6239">{item.icon}</span>
                          <span className="font-semibold text-gray-900 flex-1" data-id="element-6240">
                            {item.label}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  {/* Reguli */}
                  <div data-id="element-6241">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-6242">
                      <DogIcon className="w-5 h-5 mr-2 text-[#357BEB]" data-id="element-6243" />
                      Reguli Proprietate
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-6244">
                      <div data-id="element-6245">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6246">
                          Animale de Companie
                        </label>
                        <select value={formData.petFriendly} onChange={e => setFormData({
                      ...formData,
                      petFriendly: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6247">
                          <option value="nu" data-id="element-6248">Nu se acceptă</option>
                          <option value="da" data-id="element-6249">Se acceptă</option>
                          <option value="negociabil" data-id="element-6250">Negociabil</option>
                        </select>
                      </div>
                      <div className="flex items-end" data-id="element-6251">
                        <label className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 w-full" data-id="element-6252">
                          <input type="checkbox" checked={formData.smokingAllowed} onChange={e => setFormData({
                        ...formData,
                        smokingAllowed: e.target.checked
                      })} className="w-5 h-5 text-[#357BEB] rounded" data-id="element-6253" />
                          <span className="font-semibold text-gray-900" data-id="element-6254">
                            Fumatul Permis
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>}

            {/* Step 5: Preț & Condiții */}
            {currentStep === 5 && <motion.div key="step5" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6255">
                <div className="text-center mb-8" data-id="element-6256">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6257">
                    Preț & Condiții Financiare
                  </h1>
                  <p className="text-gray-600" data-id="element-6258">
                    Stabilește condițiile de închiriere
                  </p>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-6" data-id="element-6259">
                  {/* Preț Chirie */}
                  <div data-id="element-6260">
                    <label className="block text-lg font-bold text-gray-900 mb-3" data-id="element-6261">
                      Preț Chirie Lunară *
                    </label>
                    <div className="flex items-center space-x-4" data-id="element-6262">
                      <div className="relative flex-1" data-id="element-6263">
                        <EuroIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" data-id="element-6264" />
                        <input type="number" value={formData.price} onChange={e => setFormData({
                      ...formData,
                      price: e.target.value
                    })} placeholder="450" className="w-full pl-14 pr-4 py-4 text-2xl font-bold border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6265" />
                      </div>
                      <select value={formData.currency} onChange={e => setFormData({
                    ...formData,
                    currency: e.target.value
                  })} className="px-4 py-4 text-lg font-bold border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6266">
                        <option value="EUR" data-id="element-6267">EUR (€)</option>
                        <option value="RON" data-id="element-6268">RON (lei)</option>
                      </select>
                    </div>
                    <div className="mt-3 flex items-center space-x-4" data-id="element-6269">
                      <label className="flex items-center space-x-2 cursor-pointer" data-id="element-6270">
                        <input type="checkbox" checked={formData.negotiable} onChange={e => setFormData({
                      ...formData,
                      negotiable: e.target.checked
                    })} className="w-4 h-4 text-[#357BEB] rounded" data-id="element-6271" />
                        <span className="text-sm text-gray-700" data-id="element-6272">
                          Preț negociabil
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Utilități */}
                  <div data-id="element-6273">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-6274">
                      Utilități
                    </label>
                    <div className="space-y-3" data-id="element-6275">
                      <label className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl cursor-pointer" data-id="element-6276">
                        <input type="checkbox" checked={formData.utilitiesIncluded} onChange={e => setFormData({
                      ...formData,
                      utilitiesIncluded: e.target.checked
                    })} className="w-5 h-5 text-[#357BEB] rounded" data-id="element-6277" />
                        <span className="font-semibold text-gray-900" data-id="element-6278">
                          Utilități incluse în preț
                        </span>
                      </label>
                      {!formData.utilitiesIncluded && <div data-id="element-6279">
                          <label className="block text-sm text-gray-600 mb-2" data-id="element-6280">
                            Cost estimat utilități (opțional)
                          </label>
                          <input type="number" value={formData.utilitiesCost} onChange={e => setFormData({
                      ...formData,
                      utilitiesCost: e.target.value
                    })} placeholder="150" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6281" />
                        </div>}
                    </div>
                  </div>

                  {/* Garanție & Avans */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-6282">
                    <div data-id="element-6283">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6284">
                        Garanție (luni chirie)
                      </label>
                      <select value={formData.deposit} onChange={e => setFormData({
                    ...formData,
                    deposit: e.target.value
                  })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6285">
                        <option value="0" data-id="element-6286">Fără garanție</option>
                        <option value="1" data-id="element-6287">1 lună</option>
                        <option value="2" data-id="element-6288">2 luni</option>
                        <option value="3" data-id="element-6289">3 luni</option>
                      </select>
                    </div>
                    <div data-id="element-6290">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6291">
                        Avans (luni chirie)
                      </label>
                      <select value={formData.advancePayment} onChange={e => setFormData({
                    ...formData,
                    advancePayment: e.target.value
                  })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6292">
                        <option value="1" data-id="element-6293">1 lună</option>
                        <option value="2" data-id="element-6294">2 luni</option>
                        <option value="3" data-id="element-6295">3 luni</option>
                      </select>
                    </div>
                  </div>

                  {/* Perioadă Minimă */}
                  <div data-id="element-6296">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6297">
                      Perioadă Minimă Închiriere
                    </label>
                    <select value={formData.minimumRentalPeriod} onChange={e => setFormData({
                  ...formData,
                  minimumRentalPeriod: e.target.value
                })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6298">
                      <option value="1" data-id="element-6299">1 lună</option>
                      <option value="3" data-id="element-6300">3 luni</option>
                      <option value="6" data-id="element-6301">6 luni</option>
                      <option value="12" data-id="element-6302">12 luni (1 an)</option>
                      <option value="24" data-id="element-6303">24 luni (2 ani)</option>
                    </select>
                  </div>

                  {/* Comisioane */}
                  <div className="pt-6 border-t-2 border-gray-100" data-id="element-6304">
                    <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-6305">
                      Comisioane Agenție
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-6306">
                      <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200" data-id="element-6307">
                        <label className="block text-sm text-gray-600 mb-2" data-id="element-6308">
                          Comision Proprietar (%)
                        </label>
                        <input type="number" value={formData.ownerCommission} onChange={e => setFormData({
                      ...formData,
                      ownerCommission: e.target.value
                    })} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#357BEB]" data-id="element-6309" />
                        <p className="text-xs text-gray-500 mt-2" data-id="element-6310">+ TVA 19%</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200" data-id="element-6311">
                        <label className="block text-sm text-gray-600 mb-2" data-id="element-6312">
                          Comision Chiriaș (%)
                        </label>
                        <input type="number" value={formData.tenantCommission} onChange={e => setFormData({
                      ...formData,
                      tenantCommission: e.target.value
                    })} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#357BEB]" data-id="element-6313" />
                        <p className="text-xs text-gray-500 mt-2" data-id="element-6314">+ TVA 19%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>}

            {/* Step 6: Contact & Disponibilitate */}
            {currentStep === 6 && <motion.div key="step6" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6315">
                <div className="text-center mb-8" data-id="element-6316">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6317">
                    Date Contact & Disponibilitate
                  </h1>
                  <p className="text-gray-600" data-id="element-6318">
                    Cum te pot contacta chiriaș potențiali?
                  </p>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-6" data-id="element-6319">
                  <div data-id="element-6320">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-6321">
                      Tip Contact
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-6322">
                      <button onClick={() => setFormData({
                    ...formData,
                    ownerType: 'persoana_fizica'
                  })} className={`p-4 rounded-xl border-2 transition-all ${formData.ownerType === 'persoana_fizica' ? 'border-[#357BEB] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-6323">
                        <UserIcon className="w-6 h-6 mx-auto mb-2 text-[#357BEB]" data-id="element-6324" />
                        <p className="font-bold text-gray-900" data-id="element-6325">
                          Persoană Fizică
                        </p>
                      </button>
                      <button onClick={() => setFormData({
                    ...formData,
                    ownerType: 'persoana_juridica'
                  })} className={`p-4 rounded-xl border-2 transition-all ${formData.ownerType === 'persoana_juridica' ? 'border-[#357BEB] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-6326">
                        <BuildingIcon className="w-6 h-6 mx-auto mb-2 text-[#357BEB]" data-id="element-6327" />
                        <p className="font-bold text-gray-900" data-id="element-6328">
                          Agenție/Companie
                        </p>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6329">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6330">
                      Nume Contact *
                    </label>
                    <input type="text" value={formData.ownerName} onChange={e => setFormData({
                  ...formData,
                  ownerName: e.target.value
                })} placeholder="Ex: Ion Popescu" className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6331" />
                  </div>

                  <div data-id="element-6332">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6333">
                      Telefon *
                    </label>
                    <div className="relative" data-id="element-6334">
                      <PhoneIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-6335" />
                      <input type="tel" value={formData.ownerPhone} onChange={e => setFormData({
                    ...formData,
                    ownerPhone: e.target.value
                  })} placeholder="0721234567" className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6336" />
                    </div>
                  </div>

                  <div data-id="element-6337">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6338">
                      Email (opțional)
                    </label>
                    <div className="relative" data-id="element-6339">
                      <MailIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-6340" />
                      <input type="email" value={formData.ownerEmail} onChange={e => setFormData({
                    ...formData,
                    ownerEmail: e.target.value
                  })} placeholder="contact@email.com" className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6341" />
                    </div>
                  </div>

                  <div className="pt-6 border-t-2 border-gray-100" data-id="element-6342">
                    <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-6343">
                      Disponibilitate
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-6344">
                      <div data-id="element-6345">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6346">
                          Când este disponibilă?
                        </label>
                        <select value={formData.availability} onChange={e => setFormData({
                      ...formData,
                      availability: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6347">
                          <option value="imediat" data-id="element-6348">Imediat</option>
                          <option value="data" data-id="element-6349">De la o dată anume</option>
                        </select>
                      </div>
                      {formData.availability === 'data' && <div data-id="element-6350">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-6351">
                            Data Disponibilitate
                          </label>
                          <input type="date" value={formData.availabilityDate} onChange={e => setFormData({
                      ...formData,
                      availabilityDate: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6352" />
                        </div>}
                    </div>
                  </div>
                </div>
              </motion.div>}

            {/* Step 7: Descriere */}
            {currentStep === 7 && <motion.div key="step7" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6353">
                <div className="text-center mb-8" data-id="element-6354">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6355">
                    Descriere Anunț
                  </h1>
                  <p className="text-gray-600" data-id="element-6356">
                    Prezintă proprietatea în cel mai bun mod
                  </p>
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-6" data-id="element-6357">
                  <div data-id="element-6358">
                    <div className="flex items-center justify-between mb-3" data-id="element-6359">
                      <label className="block text-sm font-semibold text-gray-700" data-id="element-6360">
                        Titlu Anunț *
                      </label>
                      <button onClick={handleGenerateDescription} disabled={isGenerating} className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50" data-id="element-6361">
                        <SparklesIcon className="w-4 h-4" data-id="element-6362" />
                        <span data-id="element-6363">
                          {isGenerating ? 'Generez...' : 'Generează cu AI'}
                        </span>
                      </button>
                    </div>
                    <input type="text" value={formData.title} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} placeholder="Ex: Apartament 2 camere, 52mp, Titan" maxLength={80} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB]" data-id="element-6364" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-6365">
                      {formData.title.length}/80 caractere
                    </p>
                  </div>

                  <div data-id="element-6366">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-6367">
                      Descriere Completă *
                    </label>
                    <textarea value={formData.description} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} rows={16} placeholder="Descrie proprietatea în detaliu: locație, caracteristici, dotări, zona, transport..." className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#357BEB] resize-none" data-id="element-6368" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-6369">
                      {formData.description.length} caractere
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-6370">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center" data-id="element-6371">
                      <AlertCircleIcon className="w-5 h-5 mr-2 text-amber-600" data-id="element-6372" />
                      Sfaturi pentru o descriere eficientă
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-1" data-id="element-6373">
                      <li data-id="element-6374">• Menționează punctele forte ale proprietății</li>
                      <li data-id="element-6375">• Descrie zona și facilitățile din apropiere</li>
                      <li data-id="element-6376">• Specifică ce este inclus în preț</li>
                      <li data-id="element-6377">• Evită informații personale sensibile</li>
                    </ul>
                  </div>
                </div>
              </motion.div>}

            {/* Step 8: Imagini */}
            {currentStep === 8 && <motion.div key="step8" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6378">
                <div className="text-center mb-8" data-id="element-6379">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6380">
                    Fotografii Proprietate
                  </h1>
                  <p className="text-gray-600" data-id="element-6381">
                    Adaugă imagini de calitate (minim 5 recomandate)
                  </p>
                </div>

                <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" data-id="element-6382" />

                <div onClick={triggerFileInput} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-12 border-4 border-dashed border-blue-200 text-center cursor-pointer hover:border-blue-400 transition-all" data-id="element-6383">
                  <UploadIcon className="w-16 h-16 text-blue-400 mx-auto mb-4" data-id="element-6384" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6385">
                    Încarcă Fotografii
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-6386">
                    Click sau trage fișierele aici
                  </p>
                  <button type="button" className="px-8 py-3 bg-[#357BEB] text-white rounded-xl font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-6387">
                    Selectează Fișiere
                  </button>
                  <p className="text-sm text-gray-500 mt-4" data-id="element-6388">
                    JPG, PNG până la 10MB per imagine
                  </p>
                </div>

                {uploadedImages.length > 0 && <div className="bg-white rounded-2xl border-2 border-gray-200 p-6" data-id="element-6389">
                    <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-6390">
                      Imagini Încărcate ({uploadedImages.length})
                    </h3>
                    <div className="grid grid-cols-3 gap-4" data-id="element-6391">
                      {uploadedImages.map((image, index) => <div key={image.id} draggable onDragStart={() => handleImageDragStart(image.id)} onDragOver={e => handleImageDragOver(e, image.id)} onDragEnd={handleImageDragEnd} className={`group relative rounded-xl overflow-hidden border-2 ${image.isPrimary ? 'border-[#21A366] ring-4 ring-green-200' : 'border-gray-200'} hover:border-[#357BEB] transition-all cursor-move`} data-id="element-6392">
                          <div className="aspect-[4/3] bg-gray-200" data-id="element-6393">
                            <img src={image.url} alt={`Imagine ${index + 1}`} className="w-full h-full object-cover" data-id="element-6394" />
                          </div>
                          {image.isPrimary && <div className="absolute top-2 left-2 bg-[#21A366] text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center space-x-1" data-id="element-6395">
                              <StarIcon className="w-3 h-3 fill-current" data-id="element-6396" />
                              <span data-id="element-6397">Principală</span>
                            </div>}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6398">
                            <GripVerticalIcon className="w-5 h-5 text-white drop-shadow-lg" data-id="element-6399" />
                          </div>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-6400">
                            <div className="flex flex-col space-y-2" data-id="element-6401">
                              <button onClick={() => setPrimaryImage(image.id)} className="px-3 py-2 bg-white text-gray-900 rounded-lg text-xs font-semibold hover:bg-gray-100" data-id="element-6402">
                                Setează Principală
                              </button>
                              <button onClick={() => removeImage(image.id)} className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700" data-id="element-6403">
                                Șterge
                              </button>
                            </div>
                          </div>
                        </div>)}
                    </div>
                    <p className="text-sm text-gray-500 mt-4" data-id="element-6404">
                      💡 Trage imaginile pentru a le reordona. Prima imagine va
                      fi afișată ca principală.
                    </p>
                  </div>}
              </motion.div>}

            {/* Step 9: Promovare */}
            {currentStep === 9 && <motion.div key="step9" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-6405">
                <div className="text-center mb-8" data-id="element-6406">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6407">
                    Unde Publici Anunțul?
                  </h1>
                  <p className="text-gray-600" data-id="element-6408">
                    Selectează platformele pentru promovare
                  </p>
                </div>

                <div className="space-y-4" data-id="element-6409">
                  {/* Storia.ro */}
                  <label className="flex items-start space-x-4 p-6 bg-white rounded-2xl border-2 border-gray-200 cursor-pointer hover:border-orange-400 transition-all" data-id="element-6410">
                    <input type="checkbox" checked={formData.publishStoria} onChange={e => setFormData({
                  ...formData,
                  publishStoria: e.target.checked
                })} className="w-6 h-6 text-orange-500 rounded mt-1" data-id="element-6411" />
                    <div className="flex-1" data-id="element-6412">
                      <div className="flex items-center space-x-3 mb-2" data-id="element-6413">
                        <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center" data-id="element-6414">
                          <span className="text-white font-bold text-xl" data-id="element-6415">
                            S
                          </span>
                        </div>
                        <div data-id="element-6416">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-6417">
                            Storia.ro
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-6418">
                            Cea mai mare platformă de imobiliare din România
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-6419">
                        <span data-id="element-6420">✓ Vizibilitate maximă</span>
                        <span data-id="element-6421">✓ Trafic calificat</span>
                        <span data-id="element-6422">✓ Statistici detaliate</span>
                      </div>
                    </div>
                    {formData.publishStoria && <CheckCircleIcon className="w-6 h-6 text-[#21A366] flex-shrink-0" data-id="element-6423" />}
                  </label>

                  {/* OLX.ro */}
                  <label className="flex items-start space-x-4 p-6 bg-white rounded-2xl border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition-all" data-id="element-6424">
                    <input type="checkbox" checked={formData.publishOLX} onChange={e => setFormData({
                  ...formData,
                  publishOLX: e.target.checked
                })} className="w-6 h-6 text-blue-500 rounded mt-1" data-id="element-6425" />
                    <div className="flex-1" data-id="element-6426">
                      <div className="flex items-center space-x-3 mb-2" data-id="element-6427">
                        <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center" data-id="element-6428">
                          <span className="text-white font-bold text-xl" data-id="element-6429">
                            O
                          </span>
                        </div>
                        <div data-id="element-6430">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-6431">
                            OLX.ro
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-6432">
                            Platformă de anunțuri generale cu secțiune
                            imobiliare
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-6433">
                        <span data-id="element-6434">✓ Audiență largă</span>
                        <span data-id="element-6435">✓ Publicare rapidă</span>
                        <span data-id="element-6436">✓ Contact direct</span>
                      </div>
                    </div>
                    {formData.publishOLX && <CheckCircleIcon className="w-6 h-6 text-[#21A366] flex-shrink-0" data-id="element-6437" />}
                  </label>

                  {/* Imobiliare.ro */}
                  <label className="flex items-start space-x-4 p-6 bg-white rounded-2xl border-2 border-gray-200 cursor-pointer hover:border-green-400 transition-all" data-id="element-6438">
                    <input type="checkbox" checked={formData.publishImobiliare} onChange={e => setFormData({
                  ...formData,
                  publishImobiliare: e.target.checked
                })} className="w-6 h-6 text-green-500 rounded mt-1" data-id="element-6439" />
                    <div className="flex-1" data-id="element-6440">
                      <div className="flex items-center space-x-3 mb-2" data-id="element-6441">
                        <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center" data-id="element-6442">
                          <span className="text-white font-bold text-xl" data-id="element-6443">
                            I
                          </span>
                        </div>
                        <div data-id="element-6444">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-6445">
                            Imobiliare.ro
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-6446">
                            Portal dedicat exclusiv imobiliarelor
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-6447">
                        <span data-id="element-6448">✓ Clienți serioși</span>
                        <span data-id="element-6449">✓ Filtre avansate</span>
                        <span data-id="element-6450">✓ Comunitate activă</span>
                      </div>
                    </div>
                    {formData.publishImobiliare && <CheckCircleIcon className="w-6 h-6 text-[#21A366] flex-shrink-0" data-id="element-6451" />}
                  </label>

                  {/* Site Propriu */}
                  <label className="flex items-start space-x-4 p-6 bg-white rounded-2xl border-2 border-gray-200 cursor-pointer hover:border-purple-400 transition-all" data-id="element-6452">
                    <input type="checkbox" checked={formData.publishOwnSite} onChange={e => setFormData({
                  ...formData,
                  publishOwnSite: e.target.checked
                })} className="w-6 h-6 text-purple-500 rounded mt-1" data-id="element-6453" />
                    <div className="flex-1" data-id="element-6454">
                      <div className="flex items-center space-x-3 mb-2" data-id="element-6455">
                        <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center" data-id="element-6456">
                          <span className="text-white font-bold text-xl" data-id="element-6457">
                            C
                          </span>
                        </div>
                        <div data-id="element-6458">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-6459">
                            Cautlocuinta.ro
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-6460">
                            Site-ul agenției tale
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-6461">
                        <span data-id="element-6462">✓ Branding propriu</span>
                        <span data-id="element-6463">✓ Control total</span>
                        <span data-id="element-6464">✓ Fără comisioane</span>
                      </div>
                    </div>
                    {formData.publishOwnSite && <CheckCircleIcon className="w-6 h-6 text-[#21A366] flex-shrink-0" data-id="element-6465" />}
                  </label>
                </div>

                {/* Opțiuni Suplimentare */}
                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-4" data-id="element-6466">
                  <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-6467">
                    Opțiuni Promovare
                  </h3>
                  <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100" data-id="element-6468">
                    <div className="flex items-center space-x-3" data-id="element-6469">
                      <input type="checkbox" checked={formData.featuredListing} onChange={e => setFormData({
                    ...formData,
                    featuredListing: e.target.checked
                  })} className="w-5 h-5 text-[#357BEB] rounded" data-id="element-6470" />
                      <div data-id="element-6471">
                        <p className="font-semibold text-gray-900" data-id="element-6472">
                          Anunț Evidențiat
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-6473">
                          Apare în top rezultate (+cost suplimentar)
                        </p>
                      </div>
                    </div>
                  </label>
                  <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100" data-id="element-6474">
                    <div className="flex items-center space-x-3" data-id="element-6475">
                      <input type="checkbox" checked={formData.autoRenew} onChange={e => setFormData({
                    ...formData,
                    autoRenew: e.target.checked
                  })} className="w-5 h-5 text-[#357BEB] rounded" data-id="element-6476" />
                      <div data-id="element-6477">
                        <p className="font-semibold text-gray-900" data-id="element-6478">
                          Reînnoire Automată
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-6479">
                          Anunțul rămâne activ automat
                        </p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Summary */}
                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 border-2 border-green-200" data-id="element-6480">
                  <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-6481">
                    Rezumat Publicare
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700" data-id="element-6482">
                    <p data-id="element-6483">
                      ✓ Anunț va fi publicat pe{' '}
                      <strong data-id="element-6484">
                        {[formData.publishStoria, formData.publishOLX, formData.publishImobiliare, formData.publishOwnSite].filter(Boolean).length}{' '}
                        platforme
                      </strong>
                    </p>
                    <p data-id="element-6485">
                      ✓ Vizibilitate estimată:{' '}
                      <strong data-id="element-6486">1,000-5,000 vizualizări/lună</strong>
                    </p>
                    <p data-id="element-6487">
                      ✓ Durata: <strong data-id="element-6488">30 zile</strong> (cu opțiune reînnoire)
                    </p>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}